import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ErrorBoundary, ErrorFallback } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { TeamLayout } from "./layouts/TeamLayout";
import { SignIn } from "./pages/SignIn";
import { SignUp } from "./pages/SignUp";
import { VerifySignup } from "./pages/VerifySignup";
import { ChangePassword } from "./pages/ChangePassword";
import { Applications } from "./pages/Applications";
import { ApplicationDetail } from "./pages/ApplicationDetail";
import { NewApplication } from "./pages/NewApplication";
import { IntakeForm } from "./pages/IntakeForm";
import { NotFound } from "./pages/NotFound";
import "./index.css";

function RootLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorFallback />,
    children: [
      { path: "/signin", element: <SignIn /> },
      { path: "/change-password", element: <ChangePassword /> },
      // Public self-signup (Phase 2). A rep or tech joins an org with a
      // join code, verifies by email, and sets a password. Both routes
      // are public (outside ProtectedRoute) and live outside the operator
      // layout so there's no team-portal chrome for a not-yet-member.
      //
      // Each is wrapped in its own ErrorBoundary, matching the intake
      // form, so a render-time exception shows a recoverable fallback
      // rather than a blank screen.
      {
        path: "/signup",
        element: (
          <ErrorBoundary>
            <SignUp />
          </ErrorBoundary>
        ),
      },
      {
        path: "/signup/verify",
        element: (
          <ErrorBoundary>
            <VerifySignup />
          </ErrorBoundary>
        ),
      },
      // Public intake form — recipient (merchant or tech) fills out
      // the merchant application via a tokenized link. No JWT, no
      // ProtectedRoute wrapper; the token in the URL is the entire
      // access-control surface. Lives outside the operator layout so
      // there's no team-portal chrome (sidebar, etc.) visible to the
      // recipient.
      //
      // Wrapped in its own ErrorBoundary so a render-time exception
      // (e.g., a stale field shape post-deploy) doesn't show the
      // recipient a blank screen — they get a fallback with a code
      // they can quote to their rep to recover.
      {
        path: "/intake/:token",
        element: (
          <ErrorBoundary>
            <IntakeForm />
          </ErrorBoundary>
        ),
      },
      // Dev-only: pull up the full merchant intake form standalone (no auth,
      // no sidebar) for review/demo. Submission is stubbed — it renders the
      // assembled payload instead of calling AppSync. Excluded from prod
      // builds via import.meta.env.DEV (Vite sets it false in `pnpm build`).
      ...(import.meta.env.DEV
        ? [
            {
              path: "/preview/new-application",
              element: <NewApplication preview />,
            },
          ]
        : []),
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <TeamLayout />,
            children: [
              { path: "/", element: <Navigate to="/applications" replace /> },
              { path: "/applications", element: <Applications /> },
              // Static path BEFORE the parameterized one — React Router
              // v6 matches statics before dynamics, but listing first
              // makes the intent unambiguous to future readers.
              { path: "/applications/new", element: <NewApplication /> },
              {
                path: "/applications/:applicationID",
                element: <ApplicationDetail />,
              },
              { path: "*", element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </StrictMode>,
);
